import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  APIResponseAgentTurnResult,
  APIResponseAuthStatus,
  APIResponseBundlePlan,
  APIResponseCodeDescription,
  APIResponseDict,
  APIResponseLetterDiff,
  APIResponseLetterRevisionDetail,
  APIResponseLetterRevisionInfo,
  APIResponseListFieldRow,
  APIResponseListLetterRevisionInfo,
  APIResponseListProgressEntry,
  APIResponseListSessionSummary,
  APIResponseParsedDocument,
  APIResponseRefineResult,
  APIResponseSessionInfo,
  APIResponseStr,
  AppealTypeRequest,
  BodyUploadDocumentApiSessionsSessionIdDocumentsPost,
  BundlePlanRequest,
  HTTPValidationError,
  HealthApiHealthGet200,
  LoginRequest,
  ManualSaveRequest,
  RefineRequest,
  SyntheticDocumentRequest,
} from "./model";

import { customInstance } from "./mutator/custom-instance";
import type { ErrorType } from "./mutator/custom-instance";
/**
 * Report whether the gate is on and whether this browser is past it.

The SPA calls this on load to decide between the login screen and the
app. When auth is disabled (no password configured) everyone is
considered authenticated.
 * @summary Me
 */
export const meApiAuthMeGet = (signal?: AbortSignal) => {
  return customInstance<APIResponseAuthStatus>({ url: `/api/auth/me`, method: "GET", signal });
};

export const getMeApiAuthMeGetQueryKey = () => {
  return [`/api/auth/me`] as const;
};

export const getMeApiAuthMeGetQueryOptions = <
  TData = Awaited<ReturnType<typeof meApiAuthMeGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof meApiAuthMeGet>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMeApiAuthMeGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof meApiAuthMeGet>>> = ({ signal }) =>
    meApiAuthMeGet(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof meApiAuthMeGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type MeApiAuthMeGetQueryResult = NonNullable<Awaited<ReturnType<typeof meApiAuthMeGet>>>;
export type MeApiAuthMeGetQueryError = ErrorType<unknown>;

export function useMeApiAuthMeGet<
  TData = Awaited<ReturnType<typeof meApiAuthMeGet>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof meApiAuthMeGet>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof meApiAuthMeGet>>,
          TError,
          Awaited<ReturnType<typeof meApiAuthMeGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useMeApiAuthMeGet<
  TData = Awaited<ReturnType<typeof meApiAuthMeGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof meApiAuthMeGet>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof meApiAuthMeGet>>,
          TError,
          Awaited<ReturnType<typeof meApiAuthMeGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useMeApiAuthMeGet<
  TData = Awaited<ReturnType<typeof meApiAuthMeGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof meApiAuthMeGet>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Me
 */

export function useMeApiAuthMeGet<
  TData = Awaited<ReturnType<typeof meApiAuthMeGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof meApiAuthMeGet>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getMeApiAuthMeGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Login
 */
export const loginApiAuthLoginPost = (loginRequest: LoginRequest, signal?: AbortSignal) => {
  return customInstance<APIResponseAuthStatus>({
    url: `/api/auth/login`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: loginRequest,
    signal,
  });
};

export const getLoginApiAuthLoginPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof loginApiAuthLoginPost>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof loginApiAuthLoginPost>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationKey = ["loginApiAuthLoginPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof loginApiAuthLoginPost>>,
    { data: LoginRequest }
  > = (props) => {
    const { data } = props ?? {};

    return loginApiAuthLoginPost(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type LoginApiAuthLoginPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof loginApiAuthLoginPost>>
>;
export type LoginApiAuthLoginPostMutationBody = LoginRequest;
export type LoginApiAuthLoginPostMutationError = ErrorType<HTTPValidationError>;

/**
 * @summary Login
 */
export const useLoginApiAuthLoginPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof loginApiAuthLoginPost>>,
      TError,
      { data: LoginRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof loginApiAuthLoginPost>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationOptions = getLoginApiAuthLoginPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Logout
 */
export const logoutApiAuthLogoutPost = (signal?: AbortSignal) => {
  return customInstance<APIResponseAuthStatus>({ url: `/api/auth/logout`, method: "POST", signal });
};

export const getLogoutApiAuthLogoutPostMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["logoutApiAuthLogoutPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>,
    void
  > = () => {
    return logoutApiAuthLogoutPost();
  };

  return { mutationFn, ...mutationOptions };
};

export type LogoutApiAuthLogoutPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>
>;

export type LogoutApiAuthLogoutPostMutationError = ErrorType<unknown>;

/**
 * @summary Logout
 */
export const useLogoutApiAuthLogoutPost = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof logoutApiAuthLogoutPost>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getLogoutApiAuthLogoutPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * All live sessions, newest activity first. Powers the debug
session switcher (``?debug=1``). Returns lightweight summaries
only — no document payloads.
 * @summary List Sessions
 */
export const listSessionsApiSessionsGet = (signal?: AbortSignal) => {
  return customInstance<APIResponseListSessionSummary>({
    url: `/api/sessions`,
    method: "GET",
    signal,
  });
};

export const getListSessionsApiSessionsGetQueryKey = () => {
  return [`/api/sessions`] as const;
};

export const getListSessionsApiSessionsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSessionsApiSessionsGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>> = ({
    signal,
  }) => listSessionsApiSessionsGet(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListSessionsApiSessionsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSessionsApiSessionsGet>>
>;
export type ListSessionsApiSessionsGetQueryError = ErrorType<unknown>;

export function useListSessionsApiSessionsGet<
  TData = Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
          TError,
          Awaited<ReturnType<typeof listSessionsApiSessionsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListSessionsApiSessionsGet<
  TData = Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
          TError,
          Awaited<ReturnType<typeof listSessionsApiSessionsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListSessionsApiSessionsGet<
  TData = Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Sessions
 */

export function useListSessionsApiSessionsGet<
  TData = Awaited<ReturnType<typeof listSessionsApiSessionsGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSessionsApiSessionsGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListSessionsApiSessionsGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create Session
 */
export const createSessionApiSessionsPost = (signal?: AbortSignal) => {
  return customInstance<APIResponseSessionInfo>({ url: `/api/sessions`, method: "POST", signal });
};

export const getCreateSessionApiSessionsPostMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSessionApiSessionsPost>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSessionApiSessionsPost>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["createSessionApiSessionsPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSessionApiSessionsPost>>,
    void
  > = () => {
    return createSessionApiSessionsPost();
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSessionApiSessionsPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSessionApiSessionsPost>>
>;

export type CreateSessionApiSessionsPostMutationError = ErrorType<unknown>;

/**
 * @summary Create Session
 */
export const useCreateSessionApiSessionsPost = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSessionApiSessionsPost>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createSessionApiSessionsPost>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getCreateSessionApiSessionsPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get Session
 */
export const getSessionApiSessionsSessionIdGet = (sessionId: string, signal?: AbortSignal) => {
  return customInstance<APIResponseSessionInfo>({
    url: `/api/sessions/${sessionId}`,
    method: "GET",
    signal,
  });
};

export const getGetSessionApiSessionsSessionIdGetQueryKey = (sessionId?: string) => {
  return [`/api/sessions/${sessionId}`] as const;
};

export const getGetSessionApiSessionsSessionIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetSessionApiSessionsSessionIdGetQueryKey(sessionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>> = ({
    signal,
  }) => getSessionApiSessionsSessionIdGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetSessionApiSessionsSessionIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>
>;
export type GetSessionApiSessionsSessionIdGetQueryError = ErrorType<HTTPValidationError>;

export function useGetSessionApiSessionsSessionIdGet<
  TData = Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
          TError,
          Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetSessionApiSessionsSessionIdGet<
  TData = Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
          TError,
          Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetSessionApiSessionsSessionIdGet<
  TData = Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Session
 */

export function useGetSessionApiSessionsSessionIdGet<
  TData = Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSessionApiSessionsSessionIdGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetSessionApiSessionsSessionIdGetQueryOptions(sessionId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Delete Session
 */
export const deleteSessionApiSessionsSessionIdDelete = (sessionId: string) => {
  return customInstance<void>({ url: `/api/sessions/${sessionId}`, method: "DELETE" });
};

export const getDeleteSessionApiSessionsSessionIdDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>,
    TError,
    { sessionId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationKey = ["deleteSessionApiSessionsSessionIdDelete"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>,
    { sessionId: string }
  > = (props) => {
    const { sessionId } = props ?? {};

    return deleteSessionApiSessionsSessionIdDelete(sessionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteSessionApiSessionsSessionIdDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>
>;

export type DeleteSessionApiSessionsSessionIdDeleteMutationError = ErrorType<HTTPValidationError>;

/**
 * @summary Delete Session
 */
export const useDeleteSessionApiSessionsSessionIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>,
      TError,
      { sessionId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSessionApiSessionsSessionIdDelete>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationOptions = getDeleteSessionApiSessionsSessionIdDeleteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Activity log — the frontend polls this every ~1.5s while the
agent is running to render the running-state activity feed.
 * @summary Get Progress
 */
export const getProgressApiSessionsSessionIdProgressGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseListProgressEntry>({
    url: `/api/sessions/${sessionId}/progress`,
    method: "GET",
    signal,
  });
};

export const getGetProgressApiSessionsSessionIdProgressGetQueryKey = (sessionId?: string) => {
  return [`/api/sessions/${sessionId}/progress`] as const;
};

export const getGetProgressApiSessionsSessionIdProgressGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetProgressApiSessionsSessionIdProgressGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>
  > = ({ signal }) => getProgressApiSessionsSessionIdProgressGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetProgressApiSessionsSessionIdProgressGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>
>;
export type GetProgressApiSessionsSessionIdProgressGetQueryError = ErrorType<HTTPValidationError>;

export function useGetProgressApiSessionsSessionIdProgressGet<
  TData = Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
          TError,
          Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetProgressApiSessionsSessionIdProgressGet<
  TData = Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
          TError,
          Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetProgressApiSessionsSessionIdProgressGet<
  TData = Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Progress
 */

export function useGetProgressApiSessionsSessionIdProgressGet<
  TData = Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getProgressApiSessionsSessionIdProgressGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetProgressApiSessionsSessionIdProgressGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Cross-document field view — projects ``session.case`` (the
first-write-wins merged ground truth) through the same registry the
per-doc Fields tab uses.
 * @summary Get Session Fields
 */
export const getSessionFieldsApiSessionsSessionIdFieldsGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseListFieldRow>({
    url: `/api/sessions/${sessionId}/fields`,
    method: "GET",
    signal,
  });
};

export const getGetSessionFieldsApiSessionsSessionIdFieldsGetQueryKey = (sessionId?: string) => {
  return [`/api/sessions/${sessionId}/fields`] as const;
};

export const getGetSessionFieldsApiSessionsSessionIdFieldsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetSessionFieldsApiSessionsSessionIdFieldsGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>
  > = ({ signal }) => getSessionFieldsApiSessionsSessionIdFieldsGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetSessionFieldsApiSessionsSessionIdFieldsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>
>;
export type GetSessionFieldsApiSessionsSessionIdFieldsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetSessionFieldsApiSessionsSessionIdFieldsGet<
  TData = Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
          TError,
          Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetSessionFieldsApiSessionsSessionIdFieldsGet<
  TData = Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
          TError,
          Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetSessionFieldsApiSessionsSessionIdFieldsGet<
  TData = Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Session Fields
 */

export function useGetSessionFieldsApiSessionsSessionIdFieldsGet<
  TData = Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSessionFieldsApiSessionsSessionIdFieldsGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetSessionFieldsApiSessionsSessionIdFieldsGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Upload Document
 */
export const uploadDocumentApiSessionsSessionIdDocumentsPost = (
  sessionId: string,
  bodyUploadDocumentApiSessionsSessionIdDocumentsPost: BodyUploadDocumentApiSessionsSessionIdDocumentsPost,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`kind`, bodyUploadDocumentApiSessionsSessionIdDocumentsPost.kind);
  formData.append(`file`, bodyUploadDocumentApiSessionsSessionIdDocumentsPost.file);

  return customInstance<APIResponseParsedDocument>({
    url: `/api/sessions/${sessionId}/documents`,
    method: "POST",
    headers: { "Content-Type": "multipart/form-data" },
    data: formData,
    signal,
  });
};

export const getUploadDocumentApiSessionsSessionIdDocumentsPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>,
    TError,
    { sessionId: string; data: BodyUploadDocumentApiSessionsSessionIdDocumentsPost },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>,
  TError,
  { sessionId: string; data: BodyUploadDocumentApiSessionsSessionIdDocumentsPost },
  TContext
> => {
  const mutationKey = ["uploadDocumentApiSessionsSessionIdDocumentsPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>,
    { sessionId: string; data: BodyUploadDocumentApiSessionsSessionIdDocumentsPost }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return uploadDocumentApiSessionsSessionIdDocumentsPost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadDocumentApiSessionsSessionIdDocumentsPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>
>;
export type UploadDocumentApiSessionsSessionIdDocumentsPostMutationBody =
  BodyUploadDocumentApiSessionsSessionIdDocumentsPost;
export type UploadDocumentApiSessionsSessionIdDocumentsPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Upload Document
 */
export const useUploadDocumentApiSessionsSessionIdDocumentsPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>,
      TError,
      { sessionId: string; data: BodyUploadDocumentApiSessionsSessionIdDocumentsPost },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadDocumentApiSessionsSessionIdDocumentsPost>>,
  TError,
  { sessionId: string; data: BodyUploadDocumentApiSessionsSessionIdDocumentsPost },
  TContext
> => {
  const mutationOptions =
    getUploadDocumentApiSessionsSessionIdDocumentsPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lock the session's appeal-type set (one primary + any additional
reasons for a mixed denial). Idempotent for the same set; 409 if a
different set was already locked.
 * @summary Set Appeal Type
 */
export const setAppealTypeApiSessionsSessionIdAppealTypePost = (
  sessionId: string,
  appealTypeRequest: AppealTypeRequest,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseSessionInfo>({
    url: `/api/sessions/${sessionId}/appeal-type`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: appealTypeRequest,
    signal,
  });
};

export const getSetAppealTypeApiSessionsSessionIdAppealTypePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>,
    TError,
    { sessionId: string; data: AppealTypeRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>,
  TError,
  { sessionId: string; data: AppealTypeRequest },
  TContext
> => {
  const mutationKey = ["setAppealTypeApiSessionsSessionIdAppealTypePost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>,
    { sessionId: string; data: AppealTypeRequest }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return setAppealTypeApiSessionsSessionIdAppealTypePost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetAppealTypeApiSessionsSessionIdAppealTypePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>
>;
export type SetAppealTypeApiSessionsSessionIdAppealTypePostMutationBody = AppealTypeRequest;
export type SetAppealTypeApiSessionsSessionIdAppealTypePostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Set Appeal Type
 */
export const useSetAppealTypeApiSessionsSessionIdAppealTypePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>,
      TError,
      { sessionId: string; data: AppealTypeRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setAppealTypeApiSessionsSessionIdAppealTypePost>>,
  TError,
  { sessionId: string; data: AppealTypeRequest },
  TContext
> => {
  const mutationOptions =
    getSetAppealTypeApiSessionsSessionIdAppealTypePostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Generate a synthetic document aligned to the session's Case +
scenario. The first call engages the synthetic lock — from then on
real uploads return 409.
 * @summary Generate Synthetic
 */
export const generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost = (
  sessionId: string,
  syntheticDocumentRequest: SyntheticDocumentRequest,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseParsedDocument>({
    url: `/api/sessions/${sessionId}/documents/synthetic`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: syntheticDocumentRequest,
    signal,
  });
};

export const getGenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>,
    TError,
    { sessionId: string; data: SyntheticDocumentRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>,
  TError,
  { sessionId: string; data: SyntheticDocumentRequest },
  TContext
> => {
  const mutationKey = ["generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>,
    { sessionId: string; data: SyntheticDocumentRequest }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>
>;
export type GenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPostMutationBody =
  SyntheticDocumentRequest;
export type GenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Generate Synthetic
 */
export const useGenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>,
      TError,
      { sessionId: string; data: SyntheticDocumentRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof generateSyntheticApiSessionsSessionIdDocumentsSyntheticPost>>,
  TError,
  { sessionId: string; data: SyntheticDocumentRequest },
  TContext
> => {
  const mutationOptions =
    getGenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Compute the ordered document set the 'try sample data' bundle will
synthesize for the chosen appeal type(s). Read-only — it does not lock
the session or generate anything. The set is base claim docs + the
supporting documents the selection requires + branding, derived from
the same map the agent uses, so the bundle can't drift from what the
LLM expects. The frontend iterates this list against
POST .../documents/synthetic.
 * @summary Get Bundle Plan
 */
export const getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost = (
  sessionId: string,
  bundlePlanRequest: BundlePlanRequest,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseBundlePlan>({
    url: `/api/sessions/${sessionId}/documents/bundle-plan`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: bundlePlanRequest,
    signal,
  });
};

export const getGetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>,
    TError,
    { sessionId: string; data: BundlePlanRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>,
  TError,
  { sessionId: string; data: BundlePlanRequest },
  TContext
> => {
  const mutationKey = ["getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>,
    { sessionId: string; data: BundlePlanRequest }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>
>;
export type GetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPostMutationBody =
  BundlePlanRequest;
export type GetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Bundle Plan
 */
export const useGetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>,
      TError,
      { sessionId: string; data: BundlePlanRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost>>,
  TError,
  { sessionId: string; data: BundlePlanRequest },
  TContext
> => {
  const mutationOptions =
    getGetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get Document
 */
export const getDocumentApiSessionsSessionIdDocumentsDocumentIdGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseParsedDocument>({
    url: `/api/sessions/${sessionId}/documents/${documentId}`,
    method: "GET",
    signal,
  });
};

export const getGetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}`] as const;
};

export const getGetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryKey(sessionId, documentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>
  > = ({ signal }) =>
    getDocumentApiSessionsSessionIdDocumentsDocumentIdGet(sessionId, documentId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>
>;
export type GetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetDocumentApiSessionsSessionIdDocumentsDocumentIdGet<
  TData = Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
          TError,
          Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentApiSessionsSessionIdDocumentsDocumentIdGet<
  TData = Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
          TError,
          Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentApiSessionsSessionIdDocumentsDocumentIdGet<
  TData = Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Document
 */

export function useGetDocumentApiSessionsSessionIdDocumentsDocumentIdGet<
  TData = Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentApiSessionsSessionIdDocumentsDocumentIdGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetDocumentApiSessionsSessionIdDocumentsDocumentIdGetQueryOptions(
    sessionId,
    documentId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get Document Fields
 */
export const getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseListFieldRow>({
    url: `/api/sessions/${sessionId}/documents/${documentId}/fields`,
    method: "GET",
    signal,
  });
};

export const getGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}/fields`] as const;
};

export const getGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryKey(
      sessionId,
      documentId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>>
  > = ({ signal }) =>
    getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet(
      sessionId,
      documentId,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>>
  >;
export type GetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet<
  TData = Awaited<
    ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet<
  TData = Awaited<
    ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet<
  TData = Awaited<
    ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Document Fields
 */

export function useGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet<
  TData = Awaited<
    ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    getGetDocumentFieldsApiSessionsSessionIdDocumentsDocumentIdFieldsGetQueryOptions(
      sessionId,
      documentId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get Document Parsed
 */
export const getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseDict>({
    url: `/api/sessions/${sessionId}/documents/${documentId}/parsed`,
    method: "GET",
    signal,
  });
};

export const getGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}/parsed`] as const;
};

export const getGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryKey(
      sessionId,
      documentId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>>
  > = ({ signal }) =>
    getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet(
      sessionId,
      documentId,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>>
  >;
export type GetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet<
  TData = Awaited<
    ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet<
  TData = Awaited<
    ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet<
  TData = Awaited<
    ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Document Parsed
 */

export function useGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet<
  TData = Awaited<
    ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    getGetDocumentParsedApiSessionsSessionIdDocumentsDocumentIdParsedGetQueryOptions(
      sessionId,
      documentId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get Document Raw
 */
export const getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseStr>({
    url: `/api/sessions/${sessionId}/documents/${documentId}/raw`,
    method: "GET",
    signal,
  });
};

export const getGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}/raw`] as const;
};

export const getGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryKey(sessionId, documentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>
  > = ({ signal }) =>
    getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet(sessionId, documentId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>
>;
export type GetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet<
  TData = Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
          TError,
          Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet<
  TData = Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
          TError,
          Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet<
  TData = Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Document Raw
 */

export function useGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet<
  TData = Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetDocumentRawApiSessionsSessionIdDocumentsDocumentIdRawGetQueryOptions(
    sessionId,
    documentId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Serve a document for an iframe preview.

PDFs come back as raw PDF bytes (the iframe renders them natively).
EDI files come back as a basic HTML wrapper around the raw text —
PLAN.md §A6/A7 will replace this with the proper printable HTML
document renderer once the parser DTOs land.
 * @summary Preview Document
 */
export const previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/sessions/${sessionId}/documents/${documentId}/preview.html`,
    method: "GET",
    signal,
  });
};

export const getPreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}/preview.html`] as const;
};

export const getPreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryKey(
      sessionId,
      documentId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>>
  > = ({ signal }) =>
    previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet(
      sessionId,
      documentId,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type PreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>>
  >;
export type PreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryError =
  ErrorType<HTTPValidationError>;

export function usePreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet<
  TData = Awaited<
    ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function usePreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet<
  TData = Awaited<
    ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function usePreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet<
  TData = Awaited<
    ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Preview Document
 */

export function usePreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet<
  TData = Awaited<
    ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof previewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    getPreviewDocumentApiSessionsSessionIdDocumentsDocumentIdPreviewHtmlGetQueryOptions(
      sessionId,
      documentId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Serve the raw bytes for a single document as an attachment.

Unlike ``preview.html``, this always returns the on-disk bytes (raw
EDI text for 835/837, raw PDF for the rest) with an attachment
disposition so the browser saves the file instead of rendering it.
 * @summary Download Document
 */
export const downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet = (
  sessionId: string,
  documentId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/sessions/${sessionId}/documents/${documentId}/download`,
    method: "GET",
    signal,
  });
};

export const getDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryKey = (
  sessionId?: string,
  documentId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents/${documentId}/download`] as const;
};

export const getDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryKey(
      sessionId,
      documentId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>>
  > = ({ signal }) =>
    downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet(
      sessionId,
      documentId,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && documentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type DownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>>
  >;
export type DownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryError =
  ErrorType<HTTPValidationError>;

export function useDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet<
  TData = Awaited<
    ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet<
  TData = Awaited<
    ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet<
  TData = Awaited<
    ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Download Document
 */

export function useDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet<
  TData = Awaited<
    ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  documentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof downloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    getDownloadDocumentApiSessionsSessionIdDocumentsDocumentIdDownloadGetQueryOptions(
      sessionId,
      documentId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Bundle every document in the session into a single zip download.

Names that collide (e.g. two synthetic docs that share a default
filename) get a numeric suffix so the archive stays well-formed.
 * @summary Download All Documents
 */
export const downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/sessions/${sessionId}/documents.zip`,
    method: "GET",
    signal,
  });
};

export const getDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryKey = (
  sessionId?: string,
) => {
  return [`/api/sessions/${sessionId}/documents.zip`] as const;
};

export const getDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>
  > = ({ signal }) => downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type DownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>
>;
export type DownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryError =
  ErrorType<HTTPValidationError>;

export function useDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGet<
  TData = Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
          TError,
          Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGet<
  TData = Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
          TError,
          Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGet<
  TData = Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Download All Documents
 */

export function useDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGet<
  TData = Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadAllDocumentsApiSessionsSessionIdDocumentsZipGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getDownloadAllDocumentsApiSessionsSessionIdDocumentsZipGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Run Agent
 */
export const runAgentApiSessionsSessionIdRunPost = (sessionId: string, signal?: AbortSignal) => {
  return customInstance<APIResponseAgentTurnResult>({
    url: `/api/sessions/${sessionId}/run`,
    method: "POST",
    signal,
  });
};

export const getRunAgentApiSessionsSessionIdRunPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>,
    TError,
    { sessionId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationKey = ["runAgentApiSessionsSessionIdRunPost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>,
    { sessionId: string }
  > = (props) => {
    const { sessionId } = props ?? {};

    return runAgentApiSessionsSessionIdRunPost(sessionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RunAgentApiSessionsSessionIdRunPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>
>;

export type RunAgentApiSessionsSessionIdRunPostMutationError = ErrorType<HTTPValidationError>;

/**
 * @summary Run Agent
 */
export const useRunAgentApiSessionsSessionIdRunPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>,
      TError,
      { sessionId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof runAgentApiSessionsSessionIdRunPost>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationOptions = getRunAgentApiSessionsSessionIdRunPostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Refine
 */
export const refineApiSessionsSessionIdLetterRefinePost = (
  sessionId: string,
  refineRequest: RefineRequest,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseRefineResult>({
    url: `/api/sessions/${sessionId}/letter/refine`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: refineRequest,
    signal,
  });
};

export const getRefineApiSessionsSessionIdLetterRefinePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>,
    TError,
    { sessionId: string; data: RefineRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>,
  TError,
  { sessionId: string; data: RefineRequest },
  TContext
> => {
  const mutationKey = ["refineApiSessionsSessionIdLetterRefinePost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>,
    { sessionId: string; data: RefineRequest }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return refineApiSessionsSessionIdLetterRefinePost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefineApiSessionsSessionIdLetterRefinePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>
>;
export type RefineApiSessionsSessionIdLetterRefinePostMutationBody = RefineRequest;
export type RefineApiSessionsSessionIdLetterRefinePostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Refine
 */
export const useRefineApiSessionsSessionIdLetterRefinePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>,
      TError,
      { sessionId: string; data: RefineRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof refineApiSessionsSessionIdLetterRefinePost>>,
  TError,
  { sessionId: string; data: RefineRequest },
  TContext
> => {
  const mutationOptions = getRefineApiSessionsSessionIdLetterRefinePostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List Revisions
 */
export const listRevisionsApiSessionsSessionIdLetterRevisionsGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseListLetterRevisionInfo>({
    url: `/api/sessions/${sessionId}/letter/revisions`,
    method: "GET",
    signal,
  });
};

export const getListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryKey = (
  sessionId?: string,
) => {
  return [`/api/sessions/${sessionId}/letter/revisions`] as const;
};

export const getListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>
  > = ({ signal }) => listRevisionsApiSessionsSessionIdLetterRevisionsGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>
>;
export type ListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useListRevisionsApiSessionsSessionIdLetterRevisionsGet<
  TData = Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
          TError,
          Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListRevisionsApiSessionsSessionIdLetterRevisionsGet<
  TData = Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
          TError,
          Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListRevisionsApiSessionsSessionIdLetterRevisionsGet<
  TData = Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List Revisions
 */

export function useListRevisionsApiSessionsSessionIdLetterRevisionsGet<
  TData = Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listRevisionsApiSessionsSessionIdLetterRevisionsGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListRevisionsApiSessionsSessionIdLetterRevisionsGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get Revision
 */
export const getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet = (
  sessionId: string,
  revisionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseLetterRevisionDetail>({
    url: `/api/sessions/${sessionId}/letter/revisions/${revisionId}`,
    method: "GET",
    signal,
  });
};

export const getGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryKey = (
  sessionId?: string,
  revisionId?: string,
) => {
  return [`/api/sessions/${sessionId}/letter/revisions/${revisionId}`] as const;
};

export const getGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryKey(sessionId, revisionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>
  > = ({ signal }) =>
    getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet(sessionId, revisionId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && revisionId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>
>;
export type GetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet<
  TData = Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
          TError,
          Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet<
  TData = Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
          TError,
          Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet<
  TData = Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Revision
 */

export function useGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet<
  TData = Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetRevisionApiSessionsSessionIdLetterRevisionsRevisionIdGetQueryOptions(
    sessionId,
    revisionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Move the head pointer to ``revision_id``. Returns the full detail
(including html) so the frontend can switch the iframe in one
round-trip — no follow-up GET needed.
 * @summary Select Revision
 */
export const selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost = (
  sessionId: string,
  revisionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseLetterRevisionDetail>({
    url: `/api/sessions/${sessionId}/letter/revisions/${revisionId}/select`,
    method: "POST",
    signal,
  });
};

export const getSelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPostMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>
      >,
      TError,
      { sessionId: string; revisionId: string },
      TContext
    >;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>
    >,
    TError,
    { sessionId: string; revisionId: string },
    TContext
  > => {
    const mutationKey = ["selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost"];
    const { mutation: mutationOptions } = options
      ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey } };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>
      >,
      { sessionId: string; revisionId: string }
    > = (props) => {
      const { sessionId, revisionId } = props ?? {};

      return selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost(
        sessionId,
        revisionId,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type SelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>
    >
  >;

export type SelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Select Revision
 */
export const useSelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>
      >,
      TError,
      { sessionId: string; revisionId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof selectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPost>>,
  TError,
  { sessionId: string; revisionId: string },
  TContext
> => {
  const mutationOptions =
    getSelectRevisionApiSessionsSessionIdLetterRevisionsRevisionIdSelectPostMutationOptions(
      options,
    );

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Revision Diff
 */
export const revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet = (
  sessionId: string,
  revisionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseLetterDiff>({
    url: `/api/sessions/${sessionId}/letter/revisions/${revisionId}/diff`,
    method: "GET",
    signal,
  });
};

export const getRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryKey = (
  sessionId?: string,
  revisionId?: string,
) => {
  return [`/api/sessions/${sessionId}/letter/revisions/${revisionId}/diff`] as const;
};

export const getRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryKey(
      sessionId,
      revisionId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>>
  > = ({ signal }) =>
    revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet(sessionId, revisionId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(sessionId && revisionId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type RevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>>
  >;
export type RevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryError =
  ErrorType<HTTPValidationError>;

export function useRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet<
  TData = Awaited<
    ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet<
  TData = Awaited<
    ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet<
  TData = Awaited<
    ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Revision Diff
 */

export function useRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet<
  TData = Awaited<
    ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  revisionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof revisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGet>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions =
    getRevisionDiffApiSessionsSessionIdLetterRevisionsRevisionIdDiffGetQueryOptions(
      sessionId,
      revisionId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Manual Save
 */
export const manualSaveApiSessionsSessionIdLetterManualSavePost = (
  sessionId: string,
  manualSaveRequest: ManualSaveRequest,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseLetterRevisionInfo>({
    url: `/api/sessions/${sessionId}/letter/manual-save`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: manualSaveRequest,
    signal,
  });
};

export const getManualSaveApiSessionsSessionIdLetterManualSavePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>,
    TError,
    { sessionId: string; data: ManualSaveRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>,
  TError,
  { sessionId: string; data: ManualSaveRequest },
  TContext
> => {
  const mutationKey = ["manualSaveApiSessionsSessionIdLetterManualSavePost"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>,
    { sessionId: string; data: ManualSaveRequest }
  > = (props) => {
    const { sessionId, data } = props ?? {};

    return manualSaveApiSessionsSessionIdLetterManualSavePost(sessionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ManualSaveApiSessionsSessionIdLetterManualSavePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>
>;
export type ManualSaveApiSessionsSessionIdLetterManualSavePostMutationBody = ManualSaveRequest;
export type ManualSaveApiSessionsSessionIdLetterManualSavePostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Manual Save
 */
export const useManualSaveApiSessionsSessionIdLetterManualSavePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>,
      TError,
      { sessionId: string; data: ManualSaveRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof manualSaveApiSessionsSessionIdLetterManualSavePost>>,
  TError,
  { sessionId: string; data: ManualSaveRequest },
  TContext
> => {
  const mutationOptions =
    getManualSaveApiSessionsSessionIdLetterManualSavePostMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Download Pdf
 */
export const downloadPdfApiSessionsSessionIdLetterPdfGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>({
    url: `/api/sessions/${sessionId}/letter.pdf`,
    method: "GET",
    signal,
  });
};

export const getDownloadPdfApiSessionsSessionIdLetterPdfGetQueryKey = (sessionId?: string) => {
  return [`/api/sessions/${sessionId}/letter.pdf`] as const;
};

export const getDownloadPdfApiSessionsSessionIdLetterPdfGetQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getDownloadPdfApiSessionsSessionIdLetterPdfGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>
  > = ({ signal }) => downloadPdfApiSessionsSessionIdLetterPdfGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type DownloadPdfApiSessionsSessionIdLetterPdfGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>
>;
export type DownloadPdfApiSessionsSessionIdLetterPdfGetQueryError = ErrorType<HTTPValidationError>;

export function useDownloadPdfApiSessionsSessionIdLetterPdfGet<
  TData = Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
          TError,
          Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadPdfApiSessionsSessionIdLetterPdfGet<
  TData = Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
          TError,
          Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadPdfApiSessionsSessionIdLetterPdfGet<
  TData = Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Download Pdf
 */

export function useDownloadPdfApiSessionsSessionIdLetterPdfGet<
  TData = Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadPdfApiSessionsSessionIdLetterPdfGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getDownloadPdfApiSessionsSessionIdLetterPdfGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Download the current revision as a .docx (lxml-built from the
LetterModel; no external converters in the path).
 * @summary Download Docx
 */
export const downloadDocxApiSessionsSessionIdLetterDocxGet = (
  sessionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>({
    url: `/api/sessions/${sessionId}/letter.docx`,
    method: "GET",
    signal,
  });
};

export const getDownloadDocxApiSessionsSessionIdLetterDocxGetQueryKey = (sessionId?: string) => {
  return [`/api/sessions/${sessionId}/letter.docx`] as const;
};

export const getDownloadDocxApiSessionsSessionIdLetterDocxGetQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getDownloadDocxApiSessionsSessionIdLetterDocxGetQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>
  > = ({ signal }) => downloadDocxApiSessionsSessionIdLetterDocxGet(sessionId, signal);

  return { queryKey, queryFn, enabled: !!sessionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type DownloadDocxApiSessionsSessionIdLetterDocxGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>
>;
export type DownloadDocxApiSessionsSessionIdLetterDocxGetQueryError =
  ErrorType<HTTPValidationError>;

export function useDownloadDocxApiSessionsSessionIdLetterDocxGet<
  TData = Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
          TError,
          Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadDocxApiSessionsSessionIdLetterDocxGet<
  TData = Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
          TError,
          Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadDocxApiSessionsSessionIdLetterDocxGet<
  TData = Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Download Docx
 */

export function useDownloadDocxApiSessionsSessionIdLetterDocxGet<
  TData = Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadDocxApiSessionsSessionIdLetterDocxGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getDownloadDocxApiSessionsSessionIdLetterDocxGetQueryOptions(
    sessionId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Look up a medical code description. Returns 404 when the code is
not in the bundled tables; the frontend tooltip falls back to a
generic message in that case.
 * @summary Get Code Description
 */
export const getCodeDescriptionApiCodesSystemCodeGet = (
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  signal?: AbortSignal,
) => {
  return customInstance<APIResponseCodeDescription>({
    url: `/api/codes/${system}/${code}`,
    method: "GET",
    signal,
  });
};

export const getGetCodeDescriptionApiCodesSystemCodeGetQueryKey = (
  system?: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code?: string,
) => {
  return [`/api/codes/${system}/${code}`] as const;
};

export const getGetCodeDescriptionApiCodesSystemCodeGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCodeDescriptionApiCodesSystemCodeGetQueryKey(system, code);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>
  > = ({ signal }) => getCodeDescriptionApiCodesSystemCodeGet(system, code, signal);

  return { queryKey, queryFn, enabled: !!(system && code), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCodeDescriptionApiCodesSystemCodeGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>
>;
export type GetCodeDescriptionApiCodesSystemCodeGetQueryError = ErrorType<HTTPValidationError>;

export function useGetCodeDescriptionApiCodesSystemCodeGet<
  TData = Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
          TError,
          Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCodeDescriptionApiCodesSystemCodeGet<
  TData = Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
          TError,
          Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCodeDescriptionApiCodesSystemCodeGet<
  TData = Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Code Description
 */

export function useGetCodeDescriptionApiCodesSystemCodeGet<
  TData = Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  system: "icd10" | "cpt" | "hcpcs" | "modifier" | "carc" | "rarc" | "group",
  code: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCodeDescriptionApiCodesSystemCodeGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetCodeDescriptionApiCodesSystemCodeGetQueryOptions(
    system,
    code,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Health
 */
export const healthApiHealthGet = (signal?: AbortSignal) => {
  return customInstance<HealthApiHealthGet200>({ url: `/api/health`, method: "GET", signal });
};

export const getHealthApiHealthGetQueryKey = () => {
  return [`/api/health`] as const;
};

export const getHealthApiHealthGetQueryOptions = <
  TData = Awaited<ReturnType<typeof healthApiHealthGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthApiHealthGet>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthApiHealthGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthApiHealthGet>>> = ({ signal }) =>
    healthApiHealthGet(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthApiHealthGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type HealthApiHealthGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthApiHealthGet>>
>;
export type HealthApiHealthGetQueryError = ErrorType<unknown>;

export function useHealthApiHealthGet<
  TData = Awaited<ReturnType<typeof healthApiHealthGet>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthApiHealthGet>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof healthApiHealthGet>>,
          TError,
          Awaited<ReturnType<typeof healthApiHealthGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useHealthApiHealthGet<
  TData = Awaited<ReturnType<typeof healthApiHealthGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof healthApiHealthGet>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof healthApiHealthGet>>,
          TError,
          Awaited<ReturnType<typeof healthApiHealthGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useHealthApiHealthGet<
  TData = Awaited<ReturnType<typeof healthApiHealthGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthApiHealthGet>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Health
 */

export function useHealthApiHealthGet<
  TData = Awaited<ReturnType<typeof healthApiHealthGet>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof healthApiHealthGet>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getHealthApiHealthGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Spa Fallback
 */
export const spaFallbackFullPathGet = (fullPath: string, signal?: AbortSignal) => {
  return customInstance<unknown>({ url: `/${fullPath}`, method: "GET", signal });
};

export const getSpaFallbackFullPathGetQueryKey = (fullPath?: string) => {
  return [`/${fullPath}`] as const;
};

export const getSpaFallbackFullPathGetQueryOptions = <
  TData = Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  fullPath: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof spaFallbackFullPathGet>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSpaFallbackFullPathGetQueryKey(fullPath);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof spaFallbackFullPathGet>>> = ({ signal }) =>
    spaFallbackFullPathGet(fullPath, signal);

  return { queryKey, queryFn, enabled: !!fullPath, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SpaFallbackFullPathGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof spaFallbackFullPathGet>>
>;
export type SpaFallbackFullPathGetQueryError = ErrorType<HTTPValidationError>;

export function useSpaFallbackFullPathGet<
  TData = Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  fullPath: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof spaFallbackFullPathGet>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
          TError,
          Awaited<ReturnType<typeof spaFallbackFullPathGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSpaFallbackFullPathGet<
  TData = Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  fullPath: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof spaFallbackFullPathGet>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
          TError,
          Awaited<ReturnType<typeof spaFallbackFullPathGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSpaFallbackFullPathGet<
  TData = Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  fullPath: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof spaFallbackFullPathGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Spa Fallback
 */

export function useSpaFallbackFullPathGet<
  TData = Awaited<ReturnType<typeof spaFallbackFullPathGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  fullPath: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof spaFallbackFullPathGet>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getSpaFallbackFullPathGetQueryOptions(fullPath, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
